import * as React from 'react'
import { Image, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Modal from '@components/modal/Modal'
import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import TextInput from '@components/textInput/TextInput'
import Drawer from '@components/drawer/Drawer'
import Select from '@components/select/Select'

import LibraryPicker from '@components/imagePicker/ImagePicker'
import FilePicker from '@components/filePicker/FilePicker'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

const DEFAULT_MAX_FILES = 3

interface Props {
  submitStatus: Status
  onFinish: (response: EnvoieDevisIncidentRequest | StandInaccessibleRequest) => void
  title: string
  description: string
  buttonLabel: string
  maxFiles?: number
  requiredComment?: boolean
  requiredFile?: boolean
}

const CommentAndFileInput = ({
  title,
  description,
  buttonLabel,
  submitStatus,
  onFinish,
  maxFiles,
  requiredComment,
  requiredFile,
}: Props) => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [commentaire, setCommentaire] = React.useState('')
  const [files, setFiles] = React.useState<PieceJointeCommentaireRequest[]>([])

  const max = maxFiles || DEFAULT_MAX_FILES

  const addImages = (addedImages: UploadImageIncidentRequest[]) => {
    const uploadImages = addedImages.map((img) => ({ filename: img.filename, fileContent: img.image })).slice(0, max)
    setFiles((prevState) => [...prevState, ...uploadImages])
  }

  const renderForm = () => (
    <>
      <Description>{description}</Description>
      <Title>{i18n.t('screens.incidentDetail.refuseForm.comment')}</Title>
      <TextInput
        value={commentaire}
        onChange={setCommentaire}
        numberOfLines={4}
        placeholder={i18n.t('screens.incidentDetail.commentPlaceholder')}
      />
      {files.length > 0 && <ImageContainer>{files.map(renderImage)}</ImageContainer>}
      {files.length < max && (
        <>
          <FileUpload>
            <Title>{i18n.t('screens.incidentDetail.buttons.addAttachedFile')}</Title>
          </FileUpload>

          <FileInputView>
            <FilePicker onFileUploaded={addFiles}>
              <Select
                disabled
                label={i18n.t('screens.incidentDetail.import')}
                onClick={() => undefined}
                left
                icon="upload"
              />
            </FilePicker>
          </FileInputView>

          <FileInputView>
            <LibraryPicker onImageUploaded={addImages} limit={max}>
              <Select
                disabled
                label={i18n.t('screens.incidentDetail.importImage')}
                onClick={() => undefined}
                left
                icon="image"
              />
            </LibraryPicker>
          </FileInputView>
        </>
      )}
    </>
  )

  const addFiles = (addedFiles: FileUpload[]) => {
    const uploadFiles = addedFiles.map((af) => ({ filename: af.filename, fileContent: af.data })).slice(0, max)
    setFiles((prevState) => [...prevState, ...uploadFiles])
  }

  const renderImage = (image: PieceJointeCommentaireRequest) => (
    <AttachementContainer key={image.filename}>
      <Attachment source={{ uri: image.fileContent }} />
      <Delete onPress={() => deleteImage(image)}>
        <Icon name="close" size={15} color={theme.colors.background} />
      </Delete>
    </AttachementContainer>
  )

  const deleteImage = (image: PieceJointeCommentaireRequest) => {
    setFiles((prevState) => prevState.filter((p) => p.filename !== image.filename))
  }

  const validate = () => {
    if (files.length > 0) {
      const body = {
        commentaire,
        filename: files[0].filename,
        fileContent: files[0].fileContent,
      }
      onFinish(body)
    } else {
      onFinish({ commentaire })
    }
    if (isPhone) {
      Drawer.close()
    } else {
      Modal.close()
    }
  }

  const buttonStatus =
    (requiredComment && !commentaire) || (requiredFile && files.length === 0)
      ? 'disabled'
      : submitStatus === 'loading'
      ? 'loading'
      : 'active'

  if (isPhone) {
    return (
      <Container>
        <Title isCentered>{title}</Title>
        <CloseButton activeOpacity={0.9} onPress={Drawer.close}>
          <Icon name="close" size={24} color={theme.colors.primary} />
        </CloseButton>
        {renderForm()}
        <ButtonContainer disabled={buttonStatus === 'disabled'} width={width}>
          <Button status={buttonStatus} label={buttonLabel} onClick={validate} />
        </ButtonContainer>
      </Container>
    )
  }

  return (
    <ModalContainer>
      <ModalHeader>{title}</ModalHeader>

      <FormContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        {renderForm()}
      </FormContainer>
      <ModalButton>
        <Button status={buttonStatus} label={buttonLabel} onClick={validate} />
      </ModalButton>
      <CloseButton activeOpacity={0.9} onPress={() => Modal.close()}>
        <Icon name="close" size={24} color={theme.colors.primary} />
      </CloseButton>
    </ModalContainer>
  )
}

export default CommentAndFileInput

const Container = styled(View)`
  padding: 0 16px 16px;
  flex: 1;
`
const Title = styled(Text)<{ isCentered?: boolean }>`
  margin-bottom: 24px;
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.isCentered && 'text-align:center;'}
`
const ButtonContainer = styled(View)<{ width: number; disabled: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 16px;
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.disabled && 'opacity: 0.5;'}
`
const CloseButton = styled(TouchableOpacity)`
  padding: 16px;
  position: absolute;
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
      top: -18px;
      left: 0px;`
      : `
      top: 0px;
      right: 0px;`}
`
const ModalContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.incident};
  width: 650px;
  height: 500px;
`
const FormContainer = styled(ScrollView)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 35px 24px;
`
const ModalHeader = styled(Text)`
  padding: 24px 24px 36px;
  ${(props) => props.theme.fonts.Title3};
`
const ModalButton = styled(View)`
  padding: 24px;
  width: 280px;
  align-self: center;
`
const Description = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin-bottom: 32px;
`
const FileUpload = styled(View)`
  margin-top: 32px;
`

const ImageContainer = styled(View)`
  flex-direction: row;
`

const AttachementContainer = styled(View)`
  padding: 8px;
`

const Delete = styled(TouchableOpacity)`
  position: absolute;
  right: 2px;
  top: 2px;
  width: 15px;
  height: 15px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.disable};
  justify-content: center;
  align-items: center;
`

const Attachment = styled(Image)`
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 75 : 50)}px;
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 75 : 50)}px;
`

const FileInputView = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `8px 0px` : `24px`)};
`
